import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import {
  usePracticeAgentBundles,
  usePracticeAgentReleases,
  useSetBundleChannels,
  useYankBundle,
} from '../api/hooks';
import type { PracticeAgentBundle, PracticeAgentRelease } from '../api/types';
import {
  ConfirmDialog,
  PracticeAgentsHeader,
  Section,
  UNAVAILABLE,
  bytesText,
  dateText,
  practiceErrorText,
  practiceLoadError,
} from './practice-agents';

const CHANNELS = ['stable', 'pilot'] as const;

const CHANNEL_TEXT: Record<string, string> = { stable: 'Stable', pilot: 'Pilot' };

export interface ReleaseGroup {
  channel: string;
  version: string | null;
  files: PracticeAgentRelease[];
  uploadedAt: string;
  yanked: boolean;
}

/** App release files grouped by channel, then by version, newest upload first. */
export function groupReleases(items: PracticeAgentRelease[]): Map<string, ReleaseGroup[]> {
  const groups = new Map<string, ReleaseGroup>();
  for (const file of items) {
    const key = `${file.channel}\n${file.version ?? ''}`;
    const group = groups.get(key) ?? {
      channel: file.channel,
      version: file.version,
      files: [],
      uploadedAt: file.uploadedAt,
      yanked: false,
    };
    group.files.push(file);
    if (file.uploadedAt > group.uploadedAt) group.uploadedAt = file.uploadedAt;
    group.yanked = group.yanked || !!file.yankedAt;
    groups.set(key, group);
  }
  const byChannel = new Map<string, ReleaseGroup[]>();
  const order = [...CHANNELS, ...new Set(items.map(file => file.channel))];
  for (const channel of new Set(order)) {
    const list = [...groups.values()]
      .filter(group => group.channel === channel)
      .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
    if (list.length) byChannel.set(channel, list);
  }
  return byChannel;
}

export function PracticeAgentReleasesPage() {
  return (
    <div>
      <PracticeAgentsHeader />
      <Releases />
      <Bundles />
    </div>
  );
}

function Releases() {
  const releases = usePracticeAgentReleases();
  const grouped = groupReleases(releases.data ?? []);
  return (
    <Section
      title="App releases"
      description="Installer and update files per channel. The app checks each file's signature before it installs."
    >
      <QueryState isLoading={releases.isLoading} error={practiceLoadError(releases.error)}>
        {releases.data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            This Rudi release has no app releases yet.
          </p>
        ) : grouped.size === 0 ? (
          <p className="text-sm text-muted-foreground">No app releases uploaded yet.</p>
        ) : (
          <div className="space-y-4">
            {[...grouped.entries()].map(([channel, groups]) => (
              <div key={channel} className="space-y-2">
                <h3 className="text-sm font-medium">{CHANNEL_TEXT[channel] ?? channel}</h3>
                <div className="overflow-x-auto rounded-3xl border">
                  <Table aria-label={`${CHANNEL_TEXT[channel] ?? channel} releases`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Version</TableHead>
                        <TableHead>Files</TableHead>
                        <TableHead>Uploaded</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map(group => (
                        <TableRow key={`${group.channel}-${group.version ?? 'none'}`}>
                          <TableCell className="font-medium">
                            {group.version ?? 'Feed files'}
                            {group.yanked && (
                              <Badge variant="destructive" className="ml-2">
                                Yanked
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <ul className="space-y-0.5 text-xs">
                              {group.files.map(file => (
                                <li key={file.id}>
                                  {file.fileName}{' '}
                                  <span className="text-muted-foreground">
                                    {bytesText(file.size)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {dateText(group.uploadedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        )}
      </QueryState>
    </Section>
  );
}

type Pending =
  | { kind: 'stable'; bundle: PracticeAgentBundle; channels: string[] }
  | { kind: 'yank'; bundle: PracticeAgentBundle }
  | null;

function Bundles() {
  const bundles = usePracticeAgentBundles();
  const setChannels = useSetBundleChannels();
  const yank = useYankBundle();
  const [pending, setPending] = useState<Pending>(null);
  const busy = setChannels.isPending || yank.isPending;

  const saveChannels = async (bundle: PracticeAgentBundle, channels: string[]) => {
    try {
      await setChannels.mutateAsync({ version: bundle.version, channels });
      toast.success(`Bundle ${bundle.version} now on ${channels.join(' and ')}`);
      setPending(null);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not change the channels'));
    }
  };

  const toggle = (bundle: PracticeAgentBundle, channel: string, on: boolean) => {
    const channels = on
      ? [...new Set([...bundle.channels, channel])].sort()
      : bundle.channels.filter(item => item !== channel);
    if (on && channel === 'stable') setPending({ kind: 'stable', bundle, channels });
    else void saveChannels(bundle, channels);
  };

  const confirmYank = async (bundle: PracticeAgentBundle) => {
    try {
      await yank.mutateAsync(bundle.version);
      toast.success(`Bundle ${bundle.version} yanked`);
      setPending(null);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not yank the bundle'));
    }
  };

  return (
    <Section
      title="Engine bundles"
      description="Parsers, rule kinds and report builders. A computer installs the newest bundle on its channel that its app version can run, after its self-test passes."
    >
      <QueryState isLoading={bundles.isLoading} error={practiceLoadError(bundles.error)}>
        {bundles.data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            {UNAVAILABLE}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-3xl border">
            <Table aria-label="Engine bundles">
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Needs app</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(bundles.data ?? []).map(bundle => {
                  const yanked = !!bundle.yankedAt;
                  return (
                    <TableRow key={bundle.id}>
                      <TableCell className="font-medium">
                        {bundle.version}
                        {yanked && (
                          <Badge variant="destructive" className="ml-2">
                            Yanked
                          </Badge>
                        )}
                        <div className="text-xs font-normal text-muted-foreground">
                          Protocol {bundle.protocolVersion} · key {bundle.keyId}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{bundle.minAppVersion} or newer</TableCell>
                      <TableCell className="text-xs">{bytesText(bundle.size)}</TableCell>
                      <TableCell>
                        <div className="flex gap-3 text-sm">
                          {CHANNELS.map(channel => {
                            const on = bundle.channels.includes(channel);
                            return (
                              <label key={channel} className="flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  aria-label={`${CHANNEL_TEXT[channel]} for ${bundle.version}`}
                                  checked={on}
                                  disabled={yanked || busy || (on && bundle.channels.length === 1)}
                                  onChange={event => toggle(bundle, channel, event.target.checked)}
                                />
                                {CHANNEL_TEXT[channel]}
                              </label>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {dateText(bundle.uploadedAt)}
                        {bundle.yankedAt && <div>Yanked {dateText(bundle.yankedAt)}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        {!yanked && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Yank ${bundle.version}`}
                            disabled={busy}
                            onClick={() => setPending({ kind: 'yank', bundle })}
                          >
                            Yank
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {bundles.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                      No engine bundles uploaded yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </QueryState>
      <ConfirmDialog
        open={pending?.kind === 'stable'}
        title="Release to stable"
        description={
          pending?.kind === 'stable'
            ? `If ${pending.bundle.version} is the newest bundle on stable, every stable computer that can run it installs it within minutes. Pinned and frozen computers keep what they run.`
            : ''
        }
        confirmLabel={setChannels.isPending ? 'Releasing…' : 'Release to stable'}
        pending={setChannels.isPending}
        onConfirm={() =>
          pending?.kind === 'stable' && void saveChannels(pending.bundle, pending.channels)
        }
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === 'yank'}
        title="Yank bundle"
        description={
          pending?.kind === 'yank'
            ? `Rudi stops offering bundle ${pending.bundle.version}. Computers pinned to it get the newest bundle on their channel instead. Cortex cannot undo a yank.`
            : ''
        }
        confirmLabel={yank.isPending ? 'Yanking…' : 'Yank bundle'}
        pending={yank.isPending}
        destructive
        onConfirm={() => pending?.kind === 'yank' && void confirmYank(pending.bundle)}
        onClose={() => setPending(null)}
      />
    </Section>
  );
}
